import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { AutoEvaluationLabel } from '../../../../../utils/types/JobOpenings';

interface BoxItem {
  id: number;
  label: AutoEvaluationLabel;
  isEditing?: boolean;
}

interface AddRemoveComponentProps {
  initialLabels: AutoEvaluationLabel[];
  onLabelsChange: (labels: AutoEvaluationLabel[]) => void;
  disabled?: boolean;
  desirableSkills?: string[];
  title?: string;
  showBulkOperators?: boolean;
}

export const AddOrRemoveListWithOperators: React.FC<AddRemoveComponentProps> = ({
  initialLabels,
  onLabelsChange,
  disabled,
  desirableSkills,
  title,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const LF = ADMIN_PAGES_KEYS.jobOpenings.listFields;
  const [boxes, setBoxes] = useState<BoxItem[]>(() => {
    return initialLabels.map((label, index) => ({
      id: index,
      label,
      isEditing: false,
    }));
  });
  const [count, setCount] = useState(() => {
    return initialLabels.length;
  });
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const labels = boxes.map((box) => box.label);
    onLabelsChange(labels);
  }, [boxes]);

  const handleToggleOperator = (id: number) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return {
            ...box,
            label: {
              ...box.label,
              relationToNext: box.label.relationToNext === 'AND' ? 'OR' : 'AND',
            },
          };
        }
        return box;
      }),
    );
  };

  const handleAddBox = () => {
    if (inputValue.trim()) {
      const newBox: BoxItem = {
        id: count,
        label: {
          value: inputValue,
          relationToNext: 'OR',
        },
        isEditing: false,
      };
      setBoxes([...boxes, newBox]);
      setInputValue('');
      setCount((prevCount) => prevCount + 1);
    }
    setShowInput(false);
  };

  const handleRemove = (id: number) => {
    setBoxes(boxes.filter((box) => box.id !== id));
  };

  const handleStartEdit = (id: number) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return { ...box, isEditing: true };
        }
        return box;
      }),
    );
  };

  const handleStopEdit = (id: number, newLabel: string) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return {
            ...box,
            label: {
              value: newLabel,
              relationToNext: box.label.relationToNext,
            },
            isEditing: false,
          };
        }
        return box;
      }),
    );
  };

  const handleClickAdd = () => {
    setShowInput(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleAddDesirableSkills = () => {
    if (desirableSkills && desirableSkills.length > 0) {
      const existingSkills = boxes.map((box) => box.label.value);
      const newSkills = desirableSkills.filter(
        (skill) => !existingSkills.includes(skill),
      );

      if (newSkills.length > 0) {
        const newBoxes: BoxItem[] = newSkills.map((skill, index) => ({
          id: count + index,
          label: {
            value: skill,
            relationToNext: 'OR',
          },
          isEditing: false,
        }));
        setBoxes((prevBoxes) => [...prevBoxes, ...newBoxes]);
        setCount((prevCount) => prevCount + newSkills.length);
      }
    }
  };

  const handleRemoveDesirableSkills = () => {
    if (desirableSkills && desirableSkills.length > 0) {
      setBoxes((prevBoxes) =>
        prevBoxes.filter((box) => !desirableSkills.includes(box.label.value)),
      );
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 200 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={1}>
          {title}
        </Typography>
      )}

      {/* Content Area */}
      <Box sx={{ flex: 1 }}>
        {boxes.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
                color: 'grey.400',
              }}
            >
              <Plus size={24} />
            </Box>
            <Typography variant="body2" fontWeight={500} color="text.secondary" mb={0.5}>
              {t(LF.noItems)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {t(LF.addNewHint)}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            {boxes.map((box, index) => (
              <React.Fragment key={box.id}>
                <Paper
                  elevation={0}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    minWidth: 200,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: box.isEditing ? 'success.main' : 'divider',
                    bgcolor: box.isEditing ? 'success.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'success.light',
                      '& .item-actions': { opacity: 1 },
                    },
                  }}
                >
                  {box.isEditing ? (
                    <InputBase
                      defaultValue={box.label.value}
                      autoFocus
                      onBlur={(e) => handleStopEdit(box.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleStopEdit(box.id, (e.target as HTMLInputElement).value);
                        }
                      }}
                      sx={{
                        width: '100%',
                        minWidth: 200,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        flex: 1,
                        cursor: !disabled ? 'pointer' : 'default',
                        pr: 1,
                        '&:hover': { color: !disabled ? 'success.main' : 'inherit' },
                      }}
                      onClick={() => !disabled && handleStartEdit(box.id)}
                    >
                      {box.label.value}
                    </Typography>
                  )}
                  {!disabled && (
                    <IconButton
                      className="item-actions"
                      size="small"
                      color="error"
                      onClick={() => handleRemove(box.id)}
                      sx={{
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        p: 0.5,
                      }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  )}
                </Paper>

                {index < boxes.length - 1 && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    onClick={() => !disabled && handleToggleOperator(box.id)}
                    endIcon={!disabled ? <RefreshCw size={10} /> : undefined}
                    sx={{
                      minWidth: 54,
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      borderColor: 'divider',
                      color: 'text.secondary',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        borderColor: 'success.light',
                        color: 'success.dark',
                        bgcolor: 'success.50',
                      },
                    }}
                  >
                    {box.label.relationToNext}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>

      {/* Input / Add Buttons Area */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        {showInput ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              inputRef={inputRef}
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBox()}
              onBlur={handleAddBox}
              placeholder={t(LF.newItemPlaceholder)}
              sx={{ flex: 1, '& input': { fontSize: '0.8125rem' } }}
            />
            <Button
              size="small"
              onClick={() => setShowInput(false)}
              sx={{ color: 'text.secondary' }}
            >
              {t(LF.cancelInput)}
            </Button>
          </Stack>
        ) : (
          !disabled && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<Plus size={14} />}
                onClick={handleClickAdd}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderRadius: 1.5,
                }}
              >
                {t(LF.addNew)}
              </Button>
              {desirableSkills &&
                desirableSkills.length > 0 &&
                (() => {
                  const existingSkills = boxes.map((box) => box.label.value);
                  const availableToAdd = desirableSkills.filter(
                    (skill) => !existingSkills.includes(skill),
                  );
                  const alreadyAdded = desirableSkills.filter((skill) =>
                    existingSkills.includes(skill),
                  );

                  return (
                    <>
                      {availableToAdd.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<Plus size={14} />}
                          onClick={handleAddDesirableSkills}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: 1.5,
                          }}
                        >
                          {t(LF.addDesirable, { count: availableToAdd.length })}
                        </Button>
                      )}
                      {alreadyAdded.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<X size={14} />}
                          onClick={handleRemoveDesirableSkills}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: 1.5,
                          }}
                        >
                          {t(LF.removeDesirable, { count: alreadyAdded.length })}
                        </Button>
                      )}
                    </>
                  );
                })()}
            </Stack>
          )
        )}
      </Box>
    </Box>
  );
};

export default AddOrRemoveListWithOperators;
